import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

/**
 * Compact mobile card — primary actions near the top so they stay easy to find.
 */
export default function JobListCard({
  title,
  subtitle,
  chips,
  fields = [],
  actions,
  children,
  actionsFirst = true,
}) {
  const actionBlock = actions ? (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{
        mt: actionsFirst ? 1.25 : 0,
        mb: actionsFirst ? 1.25 : 0,
        '& .MuiButton-root': {
          flex: '1 1 calc(50% - 4px)',
          minWidth: 0,
          minHeight: 44,
        },
        '& .MuiChip-root': {
          flex: '1 1 100%',
          justifyContent: 'center',
        },
      }}
    >
      {actions}
    </Stack>
  ) : null;

  return (
    <Card
      sx={{
        mb: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        maxWidth: 520,
        mx: 'auto',
        width: '100%',
      }}
    >
      <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.25 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.25, wordBreak: 'break-word', fontSize: '0.8125rem' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {chips && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0, maxWidth: '45%' }}>
              {chips}
            </Stack>
          )}
        </Box>

        {actionsFirst && actionBlock}

        {fields.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1,
              mb: children ? 1 : 0,
            }}
          >
            {fields.map(({ label, value, fullWidth }) => (
              <Box key={label} sx={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{ wordBreak: 'break-word', fontSize: '0.8125rem', mt: 0.125 }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {children}

        {!actionsFirst && actionBlock}
      </CardContent>
    </Card>
  );
}
